/*
  TremShield cart sync.
  Keeps the native cart drawer fully in sync after ANY cart mutation, whoever
  triggers it (Dawn's own quantity controls, add-to-cart, or the Kaching bundles
  app recalculating tiers via its own cart calls).

  Dawn's built-in drawer refresh on the cartUpdate event only replaces
  `cart-drawer-items` + `.cart-drawer__footer`, which leaves our custom header
  count badge and free-shipping bar stale, and it can race with Kaching's
  follow-up bundle recalculation. To be source-agnostic we intercept cart
  requests and, shortly after one settles, re-render the WHOLE `.drawer__inner`
  (and the header cart bubble) from the Section Rendering API.

  Two hardening measures matter when Dawn AND Kaching both touch the cart for a
  single click:
    1. Kaching issues some of its tier recalculations over XMLHttpRequest, which
       a fetch-only interceptor never sees — so we patch XHR too. Otherwise its
       final (correct) recalc never triggers a re-render, or worse, an earlier
       stale response is the last thing written to the drawer.
    2. (2026-08-11) The old polling "reconcile" loop is GONE — it multiplied
       requests ~6x per interaction and helped trip Shopify's bot challenge.
       Convergence now: correct state first (gifts, split-line merges, both
       ok-gated and budget-capped), then paint once from fresh sections.
*/
(function () {
  if (window.__tsCartSync) return;
  window.__tsCartSync = true;

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  /* ---- free-gift scaling ------------------------------------------------
     Gift properties are written by the PDP for the pack that was added, but
     the drawer stepper can change the quantity afterwards. Gifts must follow
     the quantity, so after every mutation we recompute the correct gift set
     from the line's quantity (greedy 6/4/2 pack decomposition; remainder =
     bare alarms) and rewrite the line's properties when they differ.
     qty 10 = 6+4 -> guide, 2x lock, 1x cam, 1x sign; 12 = 6+6 -> 2x of each
     physical gift. The guide is delivered by email so it stays at 1x. The
     names/keys must exactly match what the PDP writes for 2/4/6 so a plain
     pack add needs no rewrite. */
  var TS_ALARM_PRODUCT_ID = 8025089835184;
  function tsGiftProps(qty) {
    var n = qty;
    var p6 = Math.floor(n / 6);
    n = n % 6;
    var p4 = n >= 4 ? 1 : 0;
    if (p4) n -= 4;
    var p2 = n >= 2 ? 1 : 0;
    var list = [];
    if (p6 + p4 + p2 > 0) list.push([1, 'Home Security Guide']);
    if (p6 + p4 > 0) list.push([p6 + p4, 'No-Drill Door Lock']);
    if (p6 > 0) list.push([p6, 'Fake Security Camera']);
    if (p6 > 0) list.push([p6, '24-Hour Surveillance Sign']);
    var props = {};
    function fmt(g) { return g[0] > 1 ? g[0] + '× ' + g[1] : g[1]; }
    if (list.length === 1) props['Free gift'] = fmt(list[0]);
    else list.forEach(function (g, i) { props['Free gift ' + (i + 1)] = fmt(g); });
    return props;
  }
  /* 2026-08-11 — CONSOLIDATE SPLIT LINES OF THE SAME VARIANT.
     Each PDP add attaches gift properties for THAT add's quantity, and Shopify keys cart
     lines on variant+properties — so "add a 6-pack, come back, add more" produces TWO
     lines of the same alarm (seen live: White 6 + White 11 rendering as two rows of one
     product, with the gift enforcement then correcting each line in turn). Merge them:
     remove the LAST duplicate line first (under-counting briefly is safe — the failure
     mode of the reverse order is an OVERCHARGE if the second step fails), then restore
     the remembered combined quantity on the surviving line. One mutation per pass; each
     re-enters the pipeline. tsPendingMerge survives passes so a 3-line split cannot lose
     quantity to a recomputed partial sum. */
  /* 2026-08-11 (hardened after adversarial audit) — CONSOLIDATE SPLIT LINES.
     Split lines happen because each PDP add attaches gift properties for that add's
     quantity, and Shopify keys lines on variant+properties. Fixes over the first cut:
       - the restore is CHAINED onto the removal in the SAME pass (audit 1.1: the old
         next-pass restore left a ~1.5s under-counted server cart, and tapping Secure
         Checkout inside it checked out the shrunken cart);
       - pending merges DRAIN before any new merge starts, so two variants splitting at
         once can no longer overwrite each other's owed quantity (audit 1.2);
       - pending state mirrors to sessionStorage with an expected-under guard + 30s TTL,
         so navigation mid-merge heals on the next page instead of losing a pack;
       - every mutation checks response.ok — a 429/challenge counts as FAILURE and falls
         through to painting the truth instead of retrying forever (audit 1.3);
       - lines are addressed by their stable `key`, never by index (audit 1.5). */
  var tsPendingMerge = null;
  var TS_MERGE_KEY = 'tsPendingMerge';
  /* 2026-08-11 — DELIBERATELY NOT REHYDRATED ACROSS PAGE LOADS.
     Cross-load restore was the ONLY path in this file that could RAISE a quantity, and a
     stale pending re-inflating a cart is an OVERCHARGE — the one failure direction that
     costs the customer money. An interrupted merge now simply leaves the cart
     under-counted (the buyer sees fewer items and can re-add), which is safe and visible.
     The in-page chained merge below closes the window to one round trip anyway. */
  try { sessionStorage.removeItem(TS_MERGE_KEY); } catch (e) {}
  function tsSetPending(p) {
    tsPendingMerge = p;
    try {
      if (p) sessionStorage.setItem(TS_MERGE_KEY, JSON.stringify(p));
      else sessionStorage.removeItem(TS_MERGE_KEY);
    } catch (e) {}
  }
  function tsPostCart(body) {
    tsSelfPending++;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }
  function tsConsolidateLines(cart) {
    var byVariant = {};
    cart.items.forEach(function (it, idx) {
      if (it.product_id !== TS_ALARM_PRODUCT_ID) return;
      (byVariant[it.variant_id] = byVariant[it.variant_id] || []).push(idx);
    });
    /* drain an owed restore FIRST, before any new merge is considered */
    if (tsPendingMerge) {
      var owedIdxs = byVariant[tsPendingMerge.variantId] || [];
      if (owedIdxs.length === 1) {
        var line = cart.items[owedIdxs[0]];
        if (line.quantity === tsPendingMerge.under && line.quantity !== tsPendingMerge.total) {
          var owedTotal = tsPendingMerge.total;
          return tsPostCart({ id: line.key, quantity: owedTotal }).then(function (ok) {
            if (ok) tsSetPending(null);
            return ok;
          });
        }
        tsSetPending(null); /* buyer intervened or already correct — drop it */
      } else if (owedIdxs.length === 0) {
        tsSetPending(null);
      }
      /* >1 line of the owed variant: fall through, the dup scan below continues it */
    }
    var dupIds = Object.keys(byVariant).filter(function (v) { return byVariant[v].length > 1; });
    if (!dupIds.length) return null;
    /* prefer continuing the pending variant so its owed total is never overwritten */
    var vKey = tsPendingMerge && byVariant[tsPendingMerge.variantId] && byVariant[tsPendingMerge.variantId].length > 1
      ? String(tsPendingMerge.variantId) : dupIds[0];
    var v = parseInt(vKey, 10);
    var idxs = byVariant[vKey];
    var total = idxs.reduce(function (sum, i) { return sum + cart.items[i].quantity; }, 0);
    var lastIdx = idxs[idxs.length - 1];
    var removeQty = cart.items[lastIdx].quantity;
    if (!tsPendingMerge || tsPendingMerge.variantId !== v) {
      tsSetPending({ variantId: v, total: total, under: total - removeQty, ts: Date.now() });
    } else {
      /* keep the original total; recompute only the expected under-count for THIS removal */
      tsSetPending({ variantId: v, total: tsPendingMerge.total, under: total - removeQty, ts: tsPendingMerge.ts });
    }
    var removeKey = cart.items[lastIdx].key;
    var survivorKey = cart.items[idxs[0]].key;
    var pend = tsPendingMerge;
    /* remove-first (an interrupted combine-first OVERCHARGES; interrupted remove-first
       under-counts, which sessionStorage now heals), then chain the restore immediately */
    return tsPostCart({ id: removeKey, quantity: 0 }).then(function (ok) {
      if (!ok) { tsSetPending(null); return false; }
      if (idxs.length > 2) return true; /* more dups remain; next pass continues */
      return tsPostCart({ id: survivorKey, quantity: pend.total }).then(function (ok2) {
        if (ok2) tsSetPending(null);
        return ok2 || true; /* removal happened either way; pipeline continues */
      });
    });
  }

  function tsEnforceGifts(cart) {
    /* line consolidation runs first; gifts are only enforced on a merged cart */
    var merging = tsConsolidateLines(cart);
    if (merging) return merging;

    /* 2026-08-10 — GIFTS ARE EARNED BY THE CART, NOT BY A SINGLE LINE.
       Gift tiers were computed per line item, so a buyer who took 3 White + 3 Black paid the
       correct 6-pack price ($119.94, the volume discount DOES span lines) but each line only
       saw quantity 3 — so they received the 2-pack gift only and silently lost the door lock,
       the camera and the sign: $44.97 of promised gear. Verified live before the fix.
       Now: total every alarm line, award that tier ONCE on the first alarm line, and blank
       the gift keys on the others so nothing is promised or shipped twice. */
    var totalAlarms = 0;
    var firstAlarmIndex = -1;
    for (var t = 0; t < cart.items.length; t++) {
      if (cart.items[t].product_id !== TS_ALARM_PRODUCT_ID) continue;
      totalAlarms += cart.items[t].quantity;
      if (firstAlarmIndex === -1) firstAlarmIndex = t;
    }
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.product_id !== TS_ALARM_PRODUCT_ID) continue;
      var have = it.properties || {};
      var target = {};
      Object.keys(have).forEach(function (k) {
        if (k.indexOf('Free gift') !== 0) target[k] = have[k];
      });
      /* the whole cart's tier, on the first alarm line only */
      var want = i === firstAlarmIndex ? tsGiftProps(totalAlarms) : {};
      Object.keys(want).forEach(function (k) { target[k] = want[k]; });
      /* 2026-08-10 — CLEARING GIFTS NEEDS EMPTY STRINGS, NOT AN OMITTED KEY.
         Shopify SILENTLY IGNORES a properties hash that omits a key: it means "nothing
         supplied", never "remove it". Stepping a 6-pack down to 1 produced an EMPTY target,
         so the POST changed nothing and the four gift properties survived on a $29.99 line —
         we would ship ~$45 of free gear on a single alarm, or break a promise printed on the
         cart page the buyer just read.
         It was also an UNBOUNDED LOOP: the mismatch never resolved, so every correction
         re-triggered the pipeline — measured 6 cart requests in 10s and still climbing, which
         risks tripping Shopify's own 429 bot challenge mid-purchase.
         Re-adding each stale gift key with an empty string fixes both: Shopify accepts the
         blank, all three render paths already filter on `property.last != blank` so nothing
         displays, and the comparison below normalises '' and undefined to null, so the pass
         finally reports same=true and goes quiet. */
      Object.keys(have).forEach(function (k) {
        if (k.indexOf('Free gift') === 0 && !(k in want)) target[k] = '';
      });
      var same = true;
      var all = {};
      Object.keys(have).concat(Object.keys(target)).forEach(function (k) { all[k] = 1; });
      Object.keys(all).forEach(function (k) {
        if ((have[k] || null) !== (target[k] || null)) same = false;
      });
      if (!same) {
        /* one correction per pass; its own mutation re-runs the pipeline,
           which then finds everything matching and goes quiet */
        /* by stable key, ok-gated: a 429 must NOT count as success (audit 1.3/1.5) */
        return tsPostCart({ id: it.key, quantity: it.quantity, properties: target });
      }
    }
    return Promise.resolve(false);
  }
  var debounceTimer = null;
  var reconcileTimer = null;
  var seq = 0; // increments on every cart-mutating request
  /* correction budget (audit 1.3): at most 5 self-corrections per USER mutation, then
     paint the truth and go quiet. tsSelfPending attributes interceptor hits to us. */
  var tsSelfPending = 0;
  var tsBudgetUsed = 0;

  function applySection(json, key, selector, targetEl) {
    if (!json[key] || !targetEl) return;
    var src = new DOMParser()
      .parseFromString(json[key], 'text/html')
      .querySelector(selector);
    if (src) targetEl.innerHTML = src.innerHTML;
  }

  // Authoritative item count as currently shown in the drawer header badge.
  // (Only present when the cart is non-empty, hence the null guard.)
  function domItemCount() {
    var el = document.querySelector('.ts-cart-count');
    if (!el) return null;
    var n = parseInt((el.textContent || '').trim(), 10);
    return isNaN(n) ? null : n;
  }

  // Re-render the whole drawer + header bubble from the Section Rendering API,
  // then sync the empty state from the real cart. Resolves with /cart.js.
  function render() {
    /* 2026-08-11 — CORRECT GIFTS *BEFORE* PAINTING, NOT AFTER.
       This used to paint the drawer from the server sections and only THEN call
       tsEnforceGifts, so a cart whose stored gift properties were stale rendered the
       WRONG gift list for the second or two until the correction landed. Reported live:
       after removing a line from an 18-alarm cart the drawer showed a 6-alarm line
       claiming "Includes your 10 free gifts" with 3x multipliers.
       Now: read the real cart first, and if the gift properties disagree with the cart's
       true tier, post the correction and SKIP this paint — that correction is itself a
       cart mutation, so the pipeline re-enters and paints once from correct data. */
    /* ============================================================================
       2026-08-11 — AUTOMATIC CART MUTATIONS ARE DISABLED. Read this before re-enabling.

       This layer used to rewrite cart lines by itself (gift-property corrections and
       split-line merges). That is fundamentally unsafe while a shopper is using the
       cart, for one reason: **Dawn addresses cart lines by INDEX**. Its stepper posts
       `{line: N, quantity: X}`. The moment our correction removes or reorders a line,
       the shopper's very next tap targets a line that has moved or no longer exists —
       Shopify rejects it, Dawn shows "There was an error while updating your cart", and
       the stepper desyncs from the real cart.

       Filmed live by the owner: badge 9 and total $179.91 (the REAL cart, correct)
       while the stepper flickered 15 -> 10 -> 3 with the error banner stuck on screen.
       Two earlier attempts to fix this by making the corrections smarter each produced a
       new failure (a $200 under-count window, a lost pack when both colours split, a
       request storm that tripped Shopify's 429 bot challenge).

       So: correctness now comes from NOT writing. Gift properties are metadata for
       fulfilment — they never affect price, and Shopify's own volume discount handles
       the money. Stale gift metadata on an edited line is a fulfilment note to check,
       not lost revenue; a cart the shopper cannot operate IS lost revenue.

       This layer is now display-only: after any cart mutation it re-renders the drawer
       (and, on /cart, that page's sections) from the Section Rendering API so the
       display matches the server. It issues NO cart writes of its own.

       If gift-tier automation is ever wanted again, it must (a) address lines by their
       stable `key`, never by index, (b) never run while a quantity input has focus or
       within a few seconds of a user mutation, and (c) be proven against the
       money-path-qa checklist including the decrease and remove paths.
       ============================================================================ */
    var TS_ALLOW_CART_WRITES = false;
    if (!TS_ALLOW_CART_WRITES) return renderPaint();
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* correction budget (audit 1.3): after 3 self-corrections without a fresh USER
           mutation, stop mutating and paint the truth — a persistently failing POST
           (429/challenge) must never loop. */
        /* worst observed legitimate chain: 2 variant merges + 2 gift corrections = 4
           passes (verified live with a both-colours-split cart); 5 leaves margin while
           still strangling any true runaway. */
        if (tsBudgetUsed >= 5) return false;
        return tsEnforceGifts(cart).then(function (corrected) {
          if (corrected) tsBudgetUsed++;
          return corrected;
        });
      })
      .then(function (corrected) {
        if (corrected) return null; /* pipeline re-enters; skip the discarded read */
        return renderPaint();
      });
  }

  function renderPaint() {
    /* on the /cart PAGE also refresh its two sections in the SAME request (audit 1.4:
       after a gift correction the page kept promising gear the corrected cart no longer
       carried — Dawn's paint came from the pre-correction sections response). */
    var cartItemsEl = document.getElementById('main-cart-items');
    var cartFooterEl = document.getElementById('main-cart-footer');
    var sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    if (cartItemsEl && cartItemsEl.dataset.id) sectionIds.push(cartItemsEl.dataset.id);
    if (cartFooterEl && cartFooterEl.dataset.id) sectionIds.push(cartFooterEl.dataset.id);
    return fetch('/?sections=' + sectionIds.join(','), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        applySection(
          json,
          'cart-drawer',
          '.drawer__inner',
          document.querySelector('#CartDrawer .drawer__inner')
        );
        applySection(
          json,
          'cart-icon-bubble',
          '.shopify-section',
          document.getElementById('cart-icon-bubble')
        );
        if (cartItemsEl && cartItemsEl.dataset.id) applySection(json, cartItemsEl.dataset.id, '.js-contents', cartItemsEl.querySelector('.js-contents') || cartItemsEl);
        if (cartFooterEl && cartFooterEl.dataset.id) applySection(json, cartFooterEl.dataset.id, '.js-contents', cartFooterEl.querySelector('.js-contents') || cartFooterEl);
        return fetch('/cart.js').then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);
        /* header count dot (audit 3.1): the live header is ts-header; nothing else
           updates [data-ts-cartcount] after a drawer interaction. Create it on 0->N,
           remove it at 0 (an empty visible "0" pill is worse than none). */
        try {
          var dot = document.querySelector('[data-ts-cartcount]');
          if (cart.item_count > 0) {
            if (!dot) {
              var cartBtn = document.querySelector('[data-ts-cart]');
              if (cartBtn) {
                dot = document.createElement('span');
                dot.className = 'nh-cart-dot';
                dot.setAttribute('data-ts-cartcount', '');
                cartBtn.appendChild(dot);
              }
            }
            if (dot) dot.textContent = cart.item_count;
          } else if (dot) {
            dot.remove();
          }
        } catch (e) {}
        return cart;
      });
  }

  // Watch for a late stale write clobbering our render. Re-render on drift;
  // stop once the display has matched the real cart on two consecutive reads or
  // we run out of attempts. Bails the moment a newer mutation takes ownership.
  function reconcile(startSeq, attemptsLeft, stableNeeded) {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      if (seq !== startSeq) return;
      fetch('/cart.js')
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (seq !== startSeq) return;
          var domCount = domItemCount();
          var drifted = domCount !== null && domCount !== cart.item_count;
          if (drifted) {
            render().then(function () {
              if (attemptsLeft > 1) reconcile(startSeq, attemptsLeft - 1, 2);
            });
          } else if (stableNeeded > 1 && attemptsLeft > 1) {
            reconcile(startSeq, attemptsLeft - 1, stableNeeded - 1);
          }
        })
        .catch(function () {});
    }, 500);
  }

  /* 2026-08-10 — RE-SYNC THE DRAWER ON BACK-NAVIGATION.
     The drawer markup is server-rendered at page load and only repainted when a cart
     mutation happens. A page restored from bfcache brings back the DOM exactly as it was,
     but fires no mutation — so a visitor who returns from checkout can open the cart and
     see the EMPTY drawer, with the checkout button `disabled` by Liquid's
     `{% if cart == empty %}`, while their real cart still holds every item.
     Reproduced live: /cart.js reported 6 items while the drawer rendered its empty panel
     and Secure Checkout was disabled. That reads as "my cart vanished" at the exact moment
     the buyer came back to buy.
     Only refresh when the rendered state actually disagrees with /cart.js, so a normal
     load costs nothing extra. */
  window.addEventListener('pageshow', function (event) {
    /* 2026-08-11: originally gated on event.persisted (bfcache only). WRONG — a normal
       back-navigation can also be a FULL page load served from Shopify's anonymous page
       cache, whose HTML was rendered for a different cart state (seen live: real cart 6,
       drawer painted empty, checkout disabled). One /cart.js read per pageview is cheap;
       we still repaint only when the rendered state disagrees with the truth. */
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        var rendersEmpty = drawer.classList.contains('is-empty');
        if (rendersEmpty !== (cart.item_count === 0)) refreshDrawer();
      })
      .catch(function () {});
  });

  function refreshDrawer() {
    if (debounceTimer) clearTimeout(debounceTimer);
    // debounce so Kaching's follow-up bundle recalculation settles first
    debounceTimer = setTimeout(function () {
      var startSeq = seq;
      /* 2026-08-11 — RECONCILE POLLING DISABLED. It re-polled /cart.js and re-rendered up to
         5 more times per mutation (each render = a sections fetch + a /cart.js). Combined
         with the debounce and the gift correction, ONE quantity tap could fire a dozen
         requests, and rapid tapping tripped Shopify's own 429 bot challenge ("Votre
         connexion doit être vérifiée") — locking the shopper out of the store mid-purchase,
         with a stuck "There was an error while updating your cart" banner and a desynced
         quantity. Reported live by the owner on his own phone.
         render() already re-reads /cart.js and repaints; the drift it was guarding against
         is better handled by the single pass than by polling. */
      render().catch(function () {});
    }, 900);
  }

  function noteMutation() {
    seq++;
    if (tsSelfPending > 0) tsSelfPending--;
    else tsBudgetUsed = 0; /* a USER mutation re-opens the correction budget */
    refreshDrawer();
  }

  // 1) fetch-based cart mutations (Dawn, theme, most apps)
  var originalFetch = window.fetch;
  window.fetch = function () {
    var input = arguments[0];
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = originalFetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) {
      promise
        .then(function () {
          noteMutation();
        })
        .catch(function () {});
    }
    return promise;
  };

  // 2) XHR-based cart mutations (Kaching bundles uses XHR for some recalcs)
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype && XHR.prototype.open && XHR.prototype.send) {
    var origOpen = XHR.prototype.open;
    var origSend = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      try {
        this.__tsCartMutation = CART_MUTATION.test(url || '');
      } catch (e) {
        this.__tsCartMutation = false;
      }
      return origOpen.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      if (this.__tsCartMutation) {
        this.addEventListener('loadend', function () {
          noteMutation();
        });
      }
      return origSend.apply(this, arguments);
    };
  }
})();
